import {
  Box,
  Button,
  Container,
  ContentLayout,
  FormField,
  Header,
  SpaceBetween,
  Select,
  Checkbox,
  ColumnLayout,
  TokenGroup,
  Link,
} from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BusinessUnitListDropdown from 'src/components/businessUnit/BusinessUnitListDropdown';
import { DEFAULT_LANGUAGE, LANGUAGE_LIST } from 'src/contstant/commonLabels';
import useUserProfile from 'src/hooks/useUserProfile';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import { useLocations } from 'src/store/locations/useLocations';
import { useUserPreferences } from 'src/store/userPreferences/useUserPreferences';
import { Location } from 'src/types/helios/Location';
import { BusinessUnit } from 'src/types/orbit/BusinessUnit';
import { UpdateUserPreferencesRequest } from 'src/types/orbit/UserPreferences';
import { useFormValidation } from 'src/hooks/useFormValidation';
import { ProfileSettingsValidator } from 'src/validations/ProfileSettingsValidator';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import RequiredFieldLabel from 'src/components/RequiredFieldLabel';

import LocationSelectionPanel from 'src/components/location/LocationSelectionPanel';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import { transformCodesToLocations, extractCodes } from 'src/helper/locationHelper';
import logoSrc from '../assets/logo.png';
import { useQueryClient } from '@tanstack/react-query';

const logoStyles = {
  height: '100px',
  width: 'auto',
};

interface ProfileSettingsParams {
  businessUnit?: string;
  locations: string[];
  language?: string;
  taskAlert: boolean;
  auditAlert: boolean;
  slackAlert: boolean;
  emailAlert: boolean;
}

const ProfilePreferencesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { businessUnitList, fetchBusinessUnitApiStatus, loadBusinessUnits } =
    useBusinessUnit();
  const {
    userPreferences,
    updateUserPreferencesApiStatus,
    getUserPreferencesApiStatus,
    loadUserPreferences,
    updateUserPreferences,
    clearAllUserPreferencesApiMessages,
    resetUpdateStatus,
  } = useUserPreferences();
  const { getUserProgramId, getUserId, getUserName, selectUserLanguage } =
    useUserProfile();
  const { locationsList, loadLocations } = useLocations();

  const { setContent, setTitle, closeView, isOpen } = useSplitPanel();

  const initValues: ProfileSettingsParams = {
    businessUnit: undefined,
    locations: [],
    language: DEFAULT_LANGUAGE,
    taskAlert: true,
    auditAlert: true,
    slackAlert: true,
    emailAlert: true,
  };

  const [profileParams, setProfileParams] = useState<ProfileSettingsParams>(initValues);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [requiredBusinessUnitId, setRequiredBusinessUnitId] = useState<string | null>(
    null,
  );
  const [returnUrl, setReturnUrl] = useState<string | null>(null);

  const { fieldValidationError, validate, setFieldValidationErrors } = useFormValidation(
    profileParams,
    ProfileSettingsValidator(locationsList.length),
  );

  // Check for business unit switching context from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const switchToParam = params.get('switchTo');
    const returnUrlParam = params.get('returnUrl');

    if (switchToParam) {
      setRequiredBusinessUnitId(switchToParam);
    }
    if (returnUrlParam) {
      setReturnUrl(returnUrlParam);
    }
  }, []);

  // Load initial data - always load fresh preferences like the modal does
  useEffect(() => {
    // Reset success status to prevent immediate navigation
    resetUpdateStatus();

    loadBusinessUnits({
      programId: getUserProgramId(),
      isActive: true,
      isDeleted: false,
    });
    loadUserPreferences({ programId: getUserProgramId(), userId: getUserId() });

    return () => {
      closeView();
    };
  }, []);

  useEffect(() => {
    if (profileParams.businessUnit) {
      // Only close panel on user-initiated business unit changes, not initial load
      if (!isInitialLoad && isOpen) {
        closeView();
      }

      loadLocations({
        isEnabledForBU: true,
        businessUnitId: profileParams.businessUnit,
      });

      const currentBUPrefs =
        userPreferences?.profileSettings?.businessUnitPreferences?.find(
          (pref) => pref.businessUnit === profileParams?.businessUnit,
        );

      setProfileParams((prevState) => ({
        ...prevState,
        locations: currentBUPrefs?.locations || [],
      }));
    }
  }, [profileParams.businessUnit]);

  // Update split panel content when locations change, but only if panel is already open
  // and not during initial load
  useEffect(() => {
    if (isOpen && !isInitialLoad) {
      setTitle('Select Locations');
      setContent(createLocationPanel());
    }
  }, [locationsList, profileParams.locations]);

  // Update form state when user preferences are loaded
  useEffect(() => {
    if (userPreferences) {
      const { profileSettings, notificationSettings } = userPreferences;

      // Don't auto-select the required BU - let user choose it manually
      const currentBUPrefs = profileSettings?.businessUnitPreferences?.find(
        (pref) => pref.businessUnit === profileSettings?.businessUnit,
      );

      setProfileParams((prevState) => ({
        ...prevState,
        businessUnit: profileSettings?.businessUnit || prevState.businessUnit,
        locations: currentBUPrefs?.locations || [],
        language: profileSettings?.language || DEFAULT_LANGUAGE,
        taskAlert: notificationSettings?.alertMeAbout?.task || false,
        auditAlert: notificationSettings?.alertMeAbout?.audit || false,
        slackAlert: notificationSettings?.alertCenter?.slack || false,
        emailAlert: notificationSettings?.alertCenter?.email || false,
      }));

      // Mark initial load as complete after preferences are loaded
      setIsInitialLoad(false);
    }
  }, [userPreferences]);

  const hasNoPreferences = !userPreferences?.profileSettings?.businessUnit;
  const isLoading = getUserPreferencesApiStatus.isLoading;

  // Reset form when API call is successful
  useEffect(() => {
    if (updateUserPreferencesApiStatus.isSuccess) {
      // Invalidate all queries to refetch with new business unit
      queryClient.invalidateQueries();
      clearAllUserPreferencesApiMessages();
      resetUpdateStatus();
      // Navigate to returnUrl if provided, otherwise go to home
      navigate(returnUrl || '/');
    }
  }, [
    updateUserPreferencesApiStatus.isSuccess,
    clearAllUserPreferencesApiMessages,
    resetUpdateStatus,
    navigate,
    returnUrl,
    queryClient,
  ]);

  const getLocationLinkVariant = (): 'primary' | 'secondary' => {
    return !profileParams.businessUnit || locationsList.length === 0
      ? 'secondary'
      : 'primary';
  };

  const createLocationPanel = () => (
    <LocationSelectionPanel
      selectedLocations={transformCodesToLocations(
        profileParams.locations || [],
        locationsList,
      )}
      onChange={(locations: Location[]) => {
        setProfileParams((prevState) => ({
          ...prevState,
          locations: extractCodes(locations),
        }));
      }}
      locations={locationsList}
    />
  );

  const renderFormContent = () => (
    <>
      {requiredBusinessUnitId && (
        <Container>
          <SpaceBetween direction="vertical" size="xs">
            <Box color="text-status-warning" fontSize="heading-s" fontWeight="bold">
              Switch to:{' '}
              {businessUnitList.find((bu) => bu.businessUnitId === requiredBusinessUnitId)
                ?.name || requiredBusinessUnitId}
            </Box>
            <Box color="text-body-secondary" fontSize="body-s">
              To view the shared link, select the business unit above and save your
              preferences. You can cancel to skip this.
            </Box>
          </SpaceBetween>
        </Container>
      )}
      <Container header={<Header variant="h2">Profile Settings</Header>}>
        <SpaceBetween direction="vertical" size="l">
          <FormField
            label={<RequiredFieldLabel label="Business Unit" />}
            errorText={fieldValidationError.businessUnit}
          >
            <BusinessUnitListDropdown
              businessUnitList={businessUnitList}
              isLoading={fetchBusinessUnitApiStatus.isLoading}
              selectedBusinessUnit={businessUnitList.find(
                (item) => item.businessUnitId === profileParams.businessUnit,
              )}
              error={fetchBusinessUnitApiStatus.errorMessage}
              onBusinessUnitChange={(bu: BusinessUnit) => {
                setProfileParams((prevState) => ({
                  ...prevState,
                  businessUnit: bu?.businessUnitId,
                  locations: [],
                }));
              }}
            />
          </FormField>

          <FormField
            label={
              <RequiredFieldLabel label="Locations" required={locationsList.length > 0} />
            }
            errorText={fieldValidationError.locations}
          >
            <Link
              onFollow={() => {
                setTitle('Select Locations');
                setContent(createLocationPanel());
              }}
              variant={getLocationLinkVariant()}
            >
              Choose locations
            </Link>

            <TokenGroup
              onDismiss={
                isOpen
                  ? undefined
                  : ({ detail: { itemIndex } }) => {
                      setProfileParams((prev) => ({
                        ...prev,
                        locations: [
                          ...(prev.locations || []).slice(0, itemIndex),
                          ...(prev.locations || []).slice(itemIndex + 1),
                        ],
                      }));
                    }
              }
              limit={10}
              items={(profileParams.locations || []).map((code) => ({
                value: code,
                label: code,
                dismissLabel: isOpen ? undefined : `Remove ${code}`,
              }))}
            />
          </FormField>

          <FormField label="Language">
            <Select
              options={LANGUAGE_LIST}
              selectedOption={
                LANGUAGE_LIST.find((item) => item.value === profileParams?.language) ||
                LANGUAGE_LIST.find((item) => item.value === DEFAULT_LANGUAGE)!
              }
              onChange={({ detail }) => {
                setProfileParams((prevState) => ({
                  ...prevState,
                  language: detail.selectedOption?.value as string,
                }));
              }}
              placeholder="Choose a Language"
            />
          </FormField>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Notification Settings</Header>}>
        <ColumnLayout columns={2}>
          <FormField label="Alert me about">
            <SpaceBetween direction="vertical" size="xs">
              <Checkbox
                checked={profileParams.taskAlert}
                onChange={({ detail }) =>
                  setProfileParams((prevState) => ({
                    ...prevState,
                    taskAlert: detail.checked,
                  }))
                }
              >
                Task
              </Checkbox>
              <Checkbox
                checked={profileParams.auditAlert}
                onChange={({ detail }) =>
                  setProfileParams((prevState) => ({
                    ...prevState,
                    auditAlert: detail.checked,
                  }))
                }
              >
                Audit
              </Checkbox>
            </SpaceBetween>
          </FormField>

          <FormField label="Alert Center">
            <Checkbox
              checked={profileParams.emailAlert}
              onChange={({ detail }) =>
                setProfileParams((prevState) => ({
                  ...prevState,
                  emailAlert: detail.checked,
                }))
              }
            >
              Email
            </Checkbox>
          </FormField>
        </ColumnLayout>
      </Container>
    </>
  );

  const getBusinessUnitPreferences = () => {
    if (!profileParams.businessUnit) return undefined;

    const existing = userPreferences?.profileSettings?.businessUnitPreferences || [];
    const existingIndex = existing.findIndex(
      (item) => item.businessUnit === profileParams.businessUnit,
    );

    if (existingIndex >= 0) {
      return existing.map((item, index) =>
        index === existingIndex ? { ...item, locations: profileParams.locations } : item,
      );
    }

    return [
      ...existing,
      { businessUnit: profileParams.businessUnit, locations: profileParams.locations },
    ];
  };

  const handleSave = () => {
    if (!validate()) return;

    // Validate that required business unit is selected (if applicable)
    if (requiredBusinessUnitId && profileParams.businessUnit !== requiredBusinessUnitId) {
      setFieldValidationErrors({
        businessUnit: `You must select "${
          businessUnitList.find((bu) => bu.businessUnitId === requiredBusinessUnitId)
            ?.name || requiredBusinessUnitId
        }" to continue`,
      });
      return;
    }

    // Check if business unit is changing
    const oldBusinessUnit = userPreferences?.profileSettings?.businessUnit;
    const newBusinessUnit = profileParams.businessUnit;
    const isBusinessUnitChanging =
      oldBusinessUnit && newBusinessUnit && oldBusinessUnit !== newBusinessUnit;

    const updateData: UpdateUserPreferencesRequest = {
      programId: getUserProgramId(),
      userId: getUserId(),
      profileSettings: {
        businessUnit: profileParams.businessUnit,
        businessUnitPreferences: getBusinessUnitPreferences(),
        language: profileParams.language,
      },
      notificationSettings: {
        alertMeAbout: {
          task: profileParams.taskAlert,
          audit: profileParams.auditAlert,
        },
        alertCenter: {
          slack: profileParams.slackAlert,
          email: profileParams.emailAlert,
        },
      },
      lastUpdatedBy: getUserId(),
    };
    updateUserPreferences(updateData);
    selectUserLanguage(DEFAULT_LANGUAGE);

    // Broadcast business unit change to other tabs (triggers page reload in App.tsx)
    if (isBusinessUnitChanging) {
      // NOTE: We do NOT clear filters for the old business unit
      // Filters are scoped per BU and should persist when switching back

      // Broadcast to other tabs
      localStorage.setItem(
        'businessUnitChanged',
        JSON.stringify({
          oldBusinessUnit,
          newBusinessUnit,
          timestamp: Date.now(),
        }),
      );
      // Remove the flag after a short delay so it can be used again
      setTimeout(() => {
        localStorage.removeItem('businessUnitChanged');
      }, 100);
    }
  };

  return (
    <ContentLayout>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ maxWidth: '800px', width: '100%' }}>
          <SpaceBetween direction="vertical" size="l">
            <ApiStatusFlashBar
              apiStatus={[updateUserPreferencesApiStatus]}
              clearMessages={clearAllUserPreferencesApiMessages}
            />

            {/* Logo and message section - for all users */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img src={logoSrc} alt="Gravion Logo" style={logoStyles} />
              <div style={{ marginLeft: '24px' }}>
                {hasNoPreferences ? (
                  <>
                    <Box fontSize="heading-m" fontWeight="bold" color="text-label">
                      Hello, {getUserName() || getUserId()}
                    </Box>
                    <Box fontSize="heading-l" fontWeight="heavy" color="text-label">
                      Welcome to Gravion Audit Tool
                    </Box>
                    <Box fontSize="body-m" color="text-body-secondary">
                      Please select your profile preferences to get started using the tool
                    </Box>
                  </>
                ) : (
                  <>
                    <Box fontSize="heading-l" fontWeight="heavy" color="text-label">
                      Profile Preferences
                    </Box>
                    <Box fontSize="body-m" color="text-body-secondary">
                      Manage your preferences for the tool here
                    </Box>
                  </>
                )}
              </div>
            </div>

            {renderFormContent()}

            {/* Footer Message - only for first-time users */}
            {hasNoPreferences && (
              <Box textAlign="center" color="text-body-secondary" fontSize="body-m">
                You can update these preferences later from your profile menu
              </Box>
            )}

            {/* Action Buttons */}
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                {!hasNoPreferences && (
                  <Button variant="link" onClick={() => navigate('/')}>
                    Cancel
                  </Button>
                )}
                <Button
                  variant="primary"
                  loading={
                    updateUserPreferencesApiStatus.isLoading ||
                    getUserPreferencesApiStatus.isLoading
                  }
                  onClick={handleSave}
                >
                  Save Preferences
                </Button>
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        </div>
      </div>
    </ContentLayout>
  );
};

export default ProfilePreferencesPage;
